/**
 * 展示格式化工具（服务方工作台版）。
 *
 * ══ 一条硬口径 ══
 * **缺失值一律显示「未提供」，绝不用 0 冒充。**
 * 服务方要根据工单数字安排人力与车辆，一个凭空的 0 会被读成「今天没有单」——
 * 那是会导致真实资源错配的错误。接口没给的数字，界面就不给出数字。
 *
 * 工单状态的中文名取自共享口径 `bookingStatusLabels`（与 C 端、员工控制台同一份），
 * 这里不另写映射表：一个状态 key 出现两个中文名，是跨端最难发现的缺陷。
 */
import { bookingStatusLabels } from '@hew3/shared';

/** 缺失值（字段未下发 / 为空 / 为 null）的统一文案。 */
export const MISSING_TEXT = '未提供';

/** 文本字段：null / undefined / 空串一律「未提供」。 */
export function formatText(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return MISSING_TEXT;
  return value;
}

/** 数值字段：**不做 `?? 0` 兜底** —— 缺失就是「未提供」。 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return MISSING_TEXT;
  return String(value);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return MISSING_TEXT;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return MISSING_TEXT;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 预约时间区间：两端都来自接口的号源时间，界面不推算。 */
export function slotRangeText(startAt: string | null | undefined, endAt: string | null | undefined): string {
  const from = formatDateTime(startAt);
  const to = formatDateTime(endAt);
  if (from === MISSING_TEXT && to === MISSING_TEXT) return MISSING_TEXT;
  return `${from} — ${to}`;
}

/** 工单状态中文名。取自共享口径；未知取值**原样显示**，不隐瞒服务端给了什么。 */
export function bookingStatusText(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return MISSING_TEXT;
  return (bookingStatusLabels as Record<string, string>)[status] ?? status;
}

/** 工单状态色调：终态与进行中一眼可分。 */
export function bookingStatusTone(
  status: string | null | undefined,
): 'ok' | 'warn' | 'danger' | 'ghost' | 'e' {
  if (status === 'serving') return 'e';
  if (status === 'matched' || status === 'scheduled') return 'ok';
  if (status === 'pending') return 'warn';
  if (status === 'no_show') return 'danger';
  return 'ghost';
}

/** 主体准入状态的中文名。取值来自 `core/resourceAdmission.ts` 的状态机。 */
const ADMISSION_LABELS: Record<string, string> = {
  draft: '未审核（草稿）',
  tier1_passed: '一级通过',
  tier2_passed: '二级通过',
  admitted: '三级准入通过',
  listed: '已上架（可约）',
  rejected: '已否决',
  suspended: '已暂停',
  revoked: '已清退',
};

export function admissionStatusText(status: string | null | undefined): string {
  if (!status) return MISSING_TEXT;
  return ADMISSION_LABELS[status] ?? status;
}

/** 主体类型中文名（机构 / 专家）。 */
export function subjectTypeText(subjectType: string | null | undefined): string {
  if (!subjectType) return MISSING_TEXT;
  if (subjectType === 'institution') return '机构';
  if (subjectType === 'expert') return '专家';
  return subjectType;
}

/** 从服务端锁定文案里提取剩余分钟（「…请 N 分钟后重试」）。 */
export function parseLockMinutes(message: string): number | null {
  const matched = /(\d+)\s*分钟/.exec(message);
  if (!matched) return null;
  const minutes = Number(matched[1]);
  return Number.isFinite(minutes) ? minutes : null;
}
